import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { State, Action, StateContext, Store, StateToken } from '@ngxs/store';
import { produce } from 'immer';
import { catchError, forkJoin, of, tap } from 'rxjs';
import _ from 'lodash';

import { PageConstant } from 'src/app/constants';
import {
  EnableTakeOutDelivery,
  ExitTakeOutDelivery,
  GetTakeoutDeliveryTickets,
  GetTakeoutDeliveryTicketsSuccess,
  LoadMdaTimings,
  OpenTransactionTicketUuid,
  UpdateTakeoutDeliveryFlag,
  UpdateTakeoutDeliveryStatus
} from './takeout-delivery.action';
import {
  LocationType,
  MealDeliveryAreaTiming,
  TakeoutOrderStatus
} from 'src/app/models';
import { TicketService } from 'src/app/services';
import {
  CleanSelectTicket,
  SelectBackQSTicket,
  UpdateTicketSuccess
} from '../tickets/tickets.action';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { TakeoutDeliveryService } from 'src/app/services/takeout-delivery.service';

export type MdaTimingsByDate = _.Dictionary<
  Record<number, MealDeliveryAreaTiming[]>
>;

export interface TakeOutDeliveryStateModel {
  enableTakeOutDelivery: boolean;
  // tickets: TakeOutDeliveryOrder[];
  takeoutTickets: string[];
  quickServiceTicketUuid: string;
  ticketsLoaded: boolean;
  openTransactionTicketUuid: string;
  mdaTimingsByDate: MdaTimingsByDate;
}

export const TAKEOUT_DELIVERY_STATE_TOKEN =
  new StateToken<TakeOutDeliveryStateModel>('takeout_delivery');

@State({
  name: TAKEOUT_DELIVERY_STATE_TOKEN,
  defaults: {
    enableTakeOutDelivery: false,
    takeoutTickets: [],
    quickServiceTicketUuid: null,
    ticketsLoaded: false,
    openTransactionTicketUuid: null,
    mdaTimingsByDate: {}
  }
})
@Injectable()
export class TakeOutDeliveryState {
  constructor(
    private readonly ticketService: TicketService,
    private readonly navController: NavController,
    private readonly store: Store,
    private readonly takeoutDeliveryService: TakeoutDeliveryService
  ) {}

  @Action(EnableTakeOutDelivery)
  enableTakeOutDelivery({
    patchState
  }: StateContext<TakeOutDeliveryStateModel>) {
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isQuickService: boolean = locationType === LocationType.QuickService;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;

    // allow to back to order with selected ticket for quick service
    let quickServiceTicketUuid;
    if (isQuickService) {
      quickServiceTicketUuid =
        this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;
    }
    const nextPage = PageConstant.TAKEOUT_DELIVERY_PAGE(isDiningRoom);
    patchState({ enableTakeOutDelivery: true, quickServiceTicketUuid });
    this.navController.navigateRoot(nextPage, {
      animated: true,
      animationDirection:
        nextPage === PageConstant.TAKEOUT_DELIVERY_PAGE(isDiningRoom)
          ? 'forward'
          : 'back'
    });
  }

  @Action(UpdateTakeoutDeliveryFlag)
  updateTakeoutDeliveryFlag(
    { patchState }: StateContext<TakeOutDeliveryStateModel>,
    { enableTakeOutDelivery }: UpdateTakeoutDeliveryFlag
  ) {
    // this method is used to update flag when user switch between dining room, quick service to Go to orders page.
    patchState({ enableTakeOutDelivery });
  }

  @Action(ExitTakeOutDelivery)
  exitTakeOutDelivery({
    getState,
    patchState,
    dispatch
  }: StateContext<TakeOutDeliveryStateModel>) {
    patchState({ enableTakeOutDelivery: false });
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const qsTicketUuid = getState().quickServiceTicketUuid;
    const selectedTicket =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;

    if (!isDiningRoom && (!selectedTicket || qsTicketUuid !== selectedTicket)) {
      return dispatch([
        new CleanSelectTicket(),
        new SelectBackQSTicket(qsTicketUuid)
      ]);
    }
  }

  @Action(GetTakeoutDeliveryTickets, { cancelUncompleted: true })
  getTakeoutDeliveryTicket({
    dispatch,
    patchState
  }: StateContext<TakeOutDeliveryStateModel>) {
    patchState({
      ticketsLoaded: false,
      takeoutTickets: []
    });

    return this.ticketService
      .getTakeoutTickets()
      .pipe(
        tap((tickets) =>
          dispatch(new GetTakeoutDeliveryTicketsSuccess(tickets))
        )
      );
  }

  @Action(GetTakeoutDeliveryTicketsSuccess)
  getTakeoutDeliveryTicketsSuccess(
    { patchState }: StateContext<TakeOutDeliveryStateModel>,
    { tickets }: GetTakeoutDeliveryTicketsSuccess
  ) {
    patchState({
      takeoutTickets: tickets.map((t) => t.device_ticket_uuid),
      ticketsLoaded: true
    });
  }

  @Action(UpdateTakeoutDeliveryStatus)
  updateTakeoutDeliveryStatus(
    { setState, getState, dispatch }: StateContext<TakeOutDeliveryStateModel>,
    { wsMessage }: UpdateTakeoutDeliveryStatus
  ) {
    const device_ticket_uuid = wsMessage.device_ticket_uuid;
    const wsTicketIndex = getState().takeoutTickets.indexOf(device_ticket_uuid);
    switch (wsMessage.takeout_order_status) {
      case TakeoutOrderStatus.COMPLETED:
        setState(
          produce((draft) => {
            draft.takeoutTickets = draft.takeoutTickets.filter(
              (x, index) => index !== wsTicketIndex
            );
          })
        );
        break;
      case TakeoutOrderStatus.IN_PRODUCTION:
      case TakeoutOrderStatus.SCHEDULED:
      case TakeoutOrderStatus.PRODUCTION_COMPLETE: {
        if (wsTicketIndex > -1) {
          dispatch(
            new UpdateTicketSuccess({
              device_ticket_uuid,
              takeout_order_status: wsMessage.takeout_order_status
            })
          );
        } else {
          // fetch data from server
          dispatch(new GetTakeoutDeliveryTickets());
        }
        break;
      }
    }
  }

  // @Action(LockTakeoutTicket)
  // lockTakeoutTicket(
  //   { patchState, getState }: StateContext<TakeOutDeliveryStateModel>,
  //   { ticketId }: LockTakeoutTicket
  // ) {
  //   const operator = this.store.selectSnapshot(APP_STATE_TOKEN).operator;

  //   patchState({
  //     tickets: _.map(getState().tickets, (x: TakeOutDeliveryOrder) => {
  //       if (x.device_ticket_uuid === ticketId) {
  //         return {
  //           ...x,
  //           locked: true,
  //           locked_by_operator_id: operator.id
  //         };
  //       }
  //       return x;
  //     })
  //   });

  //   return this.ticketService.lockTicket(ticketId, operator.id);
  // }

  // @Action(UnlockTakeoutTicket)
  // unlockTakeoutTicket(
  //   { getState, setState }: StateContext<TakeOutDeliveryStateModel>,
  //   { ticketId }: UnlockTakeoutTicket
  // ) {
  //   const takeoutTickets = getState().tickets;
  //   const lockedTakeoutTicketIdx = takeoutTickets.findIndex(
  //     (x) => x.device_ticket_uuid === ticketId
  //   );

  //   if (lockedTakeoutTicketIdx > -1) {
  //     setState(
  //       produce((draft) => {
  //         const draftTicket = draft.tickets[lockedTakeoutTicketIdx];
  //         draftTicket.locked = false;
  //         draftTicket.locked_by_operator_id = null;
  //       })
  //     );

  //     return this.ticketService.unlockTicket(ticketId);
  //   }
  // }

  @Action(OpenTransactionTicketUuid)
  openTransactionTicketUuid(
    { patchState }: StateContext<TakeOutDeliveryStateModel>,
    { ticketUuid }: OpenTransactionTicketUuid
  ) {
    patchState({ openTransactionTicketUuid: ticketUuid });
  }

  @Action(LoadMdaTimings)
  loadMdaTimings(
    ctx: StateContext<TakeOutDeliveryStateModel>,
    { dates }: LoadMdaTimings
  ) {
    return forkJoin(
      dates.map((date) =>
        this.takeoutDeliveryService
          .loadMdaTimings(date)
          .pipe(catchError(() => of<MealDeliveryAreaTiming[]>([])))
      )
    ).pipe(
      tap((mdaTimings) => {
        ctx.setState(
          produce((draft) => {
            mdaTimings.forEach((mdaTiming, index) => {
              draft.mdaTimingsByDate[dates[index]] = _.groupBy(
                mdaTiming,
                (x) => x.meal_id
              );
            });
          })
        );
      })
    );
  }
}
